import { BoundSelectCardGroupField, BoundSelectField, Button, Css, FormLines } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ProjectParams } from "src/routes/routesDef";
import { extractPoMessages, formatCentsToPrice, noop, queryResult } from "src/utils";
import {
  SelectOptionStep_PotentialOptionFragment,
  SelectOptionStep_PotentialOptionGroupFragment,
  useSelectOptionStepQuery,
} from "./2.SelectOptionStep.generated";
import { AddOnBookOptionFormInput } from "./addOnBookOptionWorkflow";

type SelectOptionStepProps = { formState: ObjectState<AddOnBookOptionFormInput> };
export function SelectOptionStep(props: SelectOptionStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useSelectOptionStepQuery({ variables: { projectId } });
  return queryResult(query, ({ projectReadyPlanConfig: prpc }) => (
    <div css={Css.df.fdc.gap4.$}>
      <div>
        <h2 css={Css.xl.$}>Select an Existing Option to Add</h2>
        <p css={Css.sm.gray700.$}>Select an existing product offering option to add to this project.</p>
      </div>
      <OptionSelector
        formState={formState}
        potentialOptionGroups={prpc.potentialOptionGroups}
        offeringVersion={prpc.productOffering?.aggregateCheckout?.version ?? undefined}
      />
    </div>
  ));
}

type OptionSelectorProps = {
  formState: ObjectState<AddOnBookOptionFormInput>;
  potentialOptionGroups: SelectOptionStep_PotentialOptionGroupFragment[];
  offeringVersion: number | undefined;
};
function OptionSelector(props: OptionSelectorProps) {
  const { formState, potentialOptionGroups, offeringVersion } = props;
  const [search, setSearch] = useState("");

  const { addOnCards, personalizationOptions, personalizationDisabledOptions } = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const matchesSearch = (po: SelectOptionStep_PotentialOptionFragment) =>
      !searchTerm ||
      po.option.maybeShortName.toLowerCase().includes(searchTerm) ||
      po.option.code.toLowerCase().includes(searchTerm);
    // Filter out groups the project already has an option from.
    const groups = potentialOptionGroups
      .filter((g) => !g.options.some((po) => po.isSelected))
      .sortBy((g) => g.group.order);

    // Add-ons render as radio cards; disabled ones stay visible with a tooltip explaining why.
    const addOnCards = groups
      .filter(isAddOnOrStructural)
      .flatMap((g) => g.options)
      .filter(matchesSearch)
      .map((po) => ({
        value: po.option.id,
        label: po.option.maybeShortName,
        description: <OptionCardDetail potentialOption={po} />,
        disabled: !po.canBeSelected.allowed,
        tooltip: extractPoMessages(po.canBeSelected),
      }));

    // Personalization options collapse into a single dropdown, labeled by their option group.
    const personalizations = groups
      .filter(isPersonalization)
      .flatMap((g) => g.options.filter(matchesSearch).map((po) => ({ po, groupName: g.group.name })));
    const personalizationOptions = personalizations.map(({ po, groupName }) => ({
      id: po.option.id,
      name: po.option.maybeShortName,
      groupName,
    }));
    // Disabled personalization options are shown greyed-out with the reason as a tooltip.
    const personalizationDisabledOptions = personalizations
      .filter(({ po }) => !po.canBeSelected.allowed)
      .map(({ po }) => ({ value: po.option.id, reason: extractPoMessages(po.canBeSelected) ?? "" }));

    return { addOnCards, personalizationOptions, personalizationDisabledOptions };
  }, [potentialOptionGroups, search]);

  const hasOptions = addOnCards.nonEmpty || personalizationOptions.nonEmpty;

  return (
    <div css={Css.df.fdc.gap3.$}>
      <SearchBox onSearch={setSearch} />
      {hasOptions ? (
        <>
          <p css={Css.sm.gray700.$}>
            Showing options available in the current product offering. Options already included in this project are
            hidden.
          </p>
          {/* Both inputs bind to selectedOptionId, so picking in one clears the other (single selection). */}
          {personalizationOptions.nonEmpty && (
            <BoundSelectField
              data-testid="personalizationOption"
              label="Personalization"
              options={personalizationOptions}
              field={formState.selectedOptionId}
              getOptionMenuLabel={(o) => `${o.groupName} — ${o.name}`}
              nothingSelectedText="Select a personalization option"
              disabledOptions={personalizationDisabledOptions}
            />
          )}
          {addOnCards.nonEmpty && (
            <BoundSelectCardGroupField
              view="list"
              labelStyle="hidden"
              options={addOnCards}
              field={formState.selectedOptionId}
            />
          )}
        </>
      ) : (
        <div css={Css.df.fdc.aic.jcc.gap3.py7.$}>
          <div css={Css.sm.gray700.tac.$}>
            No matching options found.
            {offeringVersion !== undefined && ` This project uses Product Offering Version ${offeringVersion}.`} Some
            newer options may not be available.
          </div>
          {/* TODO: Add link to True-Up Configuration workflow */}
          <Button label="True-Up Configuration" onClick={noop} variant="secondaryBlack" />
        </div>
      )}
    </div>
  );
}

function OptionCardDetail(props: { potentialOption: SelectOptionStep_PotentialOptionFragment }) {
  const { potentialOption } = props;
  const { option, totalCostInCents } = potentialOption;
  return (
    <FormLines width="lg" gap={1}>
      <span css={Css.xs.gray700.$}>{option.location?.pathArray.join(" > ")}</span>
      <span css={Css.xs.gray700.$}>{option.code}</span>
      <span css={Css.xs.gray700.$}>{totalCostInCents ? formatCentsToPrice(totalCostInCents) : ""}</span>
    </FormLines>
  );
}

// Add-on and structural groups render as radio cards.
function isAddOnOrStructural(pog: SelectOptionStep_PotentialOptionGroupFragment): boolean {
  const { type } = pog.group.group;
  return !!type.isAddOnV2 || !!type.isStructuralV2;
}

// Product / package groups render in the personalization dropdown.
function isPersonalization(pog: SelectOptionStep_PotentialOptionGroupFragment): boolean {
  const { type } = pog.group.group;
  return type.isProductOptionV2 || type.isPackageOptionV2;
}
